import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { buildMeta } from "./metadata.mjs";
import { SITE_ORIGIN } from "./config.mjs";

/**
 * Keeps the <head> correct during client-side navigation.
 *
 * The server already spliced the right tags into the HTML for the URL the
 * visitor landed on (see api/render.mjs). But this is a SPA: navigating from
 * /shop to a product never touches the server, so without this the title, the
 * canonical and the JSON-LD would all still describe the landing page. That is
 * what GA4 sees, what a browser tab shows, and what any crawler that does run
 * JS re-reads on the rendered page.
 *
 * Both sides call the same buildMeta, so the tags cannot drift.
 */
const Seo = ({ type, data }) => {
  const location = useLocation();
  const meta = buildMeta({
    type,
    path: location.pathname,
    data,
    origin: SITE_ORIGIN,
  });

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={meta.robots} />
      <link rel="canonical" href={meta.canonical} />

      <meta property="og:type" content={meta.ogType} />
      <meta property="og:url" content={meta.canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      {meta.image && <meta property="og:image" content={meta.image} />}
      {meta.imageAlt && <meta property="og:image:alt" content={meta.imageAlt} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      {meta.image && <meta name="twitter:image" content={meta.image} />}

      {(meta.jsonLd || []).map((block, index) => (
        <script type="application/ld+json" key={index}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
